/**
 * Add or edit a milestone.
 *
 * A short form, because a milestone is a short thing: a name, a date, an amount
 * and where it has got to. The amount is what makes it billable on its own —
 * `add-milestone` on an invoice pulls exactly this figure across.
 *
 * `reached_on` is not a field. The status is, and the server stamps the date
 * when the status becomes `reached`: two inputs that have to agree are two
 * inputs that will eventually disagree.
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { ApiError, Alert, Button, Input, Modal, Select, Textarea, api, toast } from '@connect/web-shared';

const schema = z.object({
  name: z.string().trim().min(1, 'A milestone needs a name.').max(160),
  status: z.enum(['pending', 'reached', 'invoiced', 'cancelled']),
  due_on: z.string().optional(),
  amount: z
    .string()
    .trim()
    .regex(/^\d*\.?\d{0,2}$/, 'Enter an amount, to two decimal places.')
    .optional(),
  notes: z.string().trim().optional(),
});

type FormValues = z.infer<typeof schema>;

export interface MilestoneRecord {
  public_id: string;
  name: string;
  status: string;
  due_on: string | null;
  amount: string | null;
  notes: string | null;
}

const DEFAULTS: FormValues = {
  name: '',
  status: 'pending',
  due_on: '',
  amount: '',
  notes: '',
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'reached', label: 'Reached' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function MilestoneFormDialog({
  open,
  onClose,
  onSaved,
  projectPublicId,
  currency,
  milestone,
}: {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  projectPublicId: string;
  currency: string;
  /** The milestone being edited, or `null` to add one. */
  milestone: MilestoneRecord | null;
}) {
  const editing = milestone !== null;
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULTS });

  useEffect(() => {
    if (!open) return;
    form.reset(
      milestone
        ? {
            name: milestone.name,
            status: milestone.status as FormValues['status'],
            due_on: milestone.due_on ?? '',
            amount: milestone.amount ?? '',
            notes: milestone.notes ?? '',
          }
        : DEFAULTS,
    );
  }, [open, milestone, form]);

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const payload = Object.fromEntries(
        Object.entries(values).map(([key, value]) => [
          key,
          typeof value === 'string' && value.trim() === '' ? null : value,
        ]),
      );
      if (editing) {
        return api.patch(`/projects/milestones/${milestone.public_id}`, payload);
      }
      return api.post(`/projects/${projectPublicId}/milestones`, payload);
    },
    onSuccess: () => {
      toast.success(editing ? 'Milestone updated' : 'Milestone added');
      onSaved?.();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        let placed = false;
        for (const [field, message] of Object.entries(error.fieldErrors)) {
          if (field in DEFAULTS) {
            form.setError(field as keyof FormValues, { message });
            placed = true;
          }
        }
        if (!placed) form.setError('root', { message: error.message });
        return;
      }
      form.setError('root', { message: 'Could not reach the server. Try again.' });
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit milestone' : 'New milestone'}
      size="md"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={save.isPending}
            onClick={() => void form.handleSubmit((values) => save.mutateAsync(values))()}
          >
            {editing ? 'Save changes' : 'Add milestone'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit((values) => save.mutateAsync(values))();
        }}
      >
        {form.formState.errors.root?.message ? (
          <Alert tone="danger">{form.formState.errors.root.message}</Alert>
        ) : null}

        {/* An invoiced milestone is the one state this form cannot set or
            leave: it is set by raising an invoice against it. */}
        {milestone?.status === 'invoiced' ? (
          <Alert tone="info" title="Already invoiced">
            The amount and the status are fixed now. Correcting an invoiced milestone means a
            credit note against the invoice that carried it.
          </Alert>
        ) : null}

        <Input
          label="Name"
          required
          placeholder="Phase 1 sign-off"
          error={form.formState.errors.name?.message}
          {...form.register('name')}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Status"
            options={STATUS_OPTIONS}
            hint="The date it was reached is stamped for you."
            disabled={milestone?.status === 'invoiced'}
            error={form.formState.errors.status?.message}
            {...form.register('status')}
          />
          <Input
            label="Due"
            type="date"
            error={form.formState.errors.due_on?.message}
            {...form.register('due_on')}
          />
          <Input
            label={`Amount (${currency})`}
            inputMode="decimal"
            placeholder="0.00"
            hint="What this stage is worth, for a fixed-price invoice."
            disabled={milestone?.status === 'invoiced'}
            error={form.formState.errors.amount?.message}
            {...form.register('amount')}
          />
        </div>

        <Textarea
          label="Notes"
          rows={2}
          error={form.formState.errors.notes?.message}
          {...form.register('notes')}
        />
      </form>
    </Modal>
  );
}
